const imageUrls = import.meta.glob(
  ['./images/desktop-optimized/*.png', './images/mobile-optimized/*.png'],
  {
    eager: true,
    query: '?url',
    import: 'default',
  }
);

const loadImage = function (url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (err) => reject(err));
    img.src = url;
    img.alt = '';
  });
};

const mediaQueryList = window.matchMedia('(min-width: 768px)');

function getBreakpoint() {
  return mediaQueryList.matches ? 'desktop' : 'mobile';
}

class BogaSupplyCurveChart extends HTMLElement {
  constructor() {
    super();
  }

  async connectedCallback() {
    this.figureNode = this.querySelector('[data-ref="figure"]');
    this.playButtonNode = this.querySelector('[data-ref="play"]');
    this.tooltipNode = this.querySelector('[data-ref="tooltip"]');
    this.yearInputNode = this.querySelector('input[name="year"]');
    this.keyNode = this.querySelector('[data-ref="key"]');
    this.yAxisNode = this.querySelector('[data-ref="yaxis"]');
    this.xAxisNode = this.querySelector('[data-ref="xaxis"]');
    this.timelineInputNode = this.querySelector('input[name="year"]');

    const state = this._getState();

    this.products = [...this.querySelectorAll('input[name="product"]')].map((el) => el.value);
    this.scenarios = [...this.querySelectorAll('input[name="scenario"]')].map((el) => el.value);
    this.years = Array.from(
      { length: (state.yearMax - state.yearMin) / state.yearStep + 1 },
      (_, i) => state.yearMin + i * state.yearStep
    );

    this.addEventListener('change', this._handleChange.bind(this));
    this.addEventListener('input', this._handleInput.bind(this));
    this.playButtonNode.addEventListener('click', this._handleClickPlay.bind(this));
    // Stop playback if user clicks the timeline input
    this.timelineInputNode.addEventListener('click', () => {
      this._stopPlayback();
    });

    mediaQueryList.addEventListener('change', async (event) => {
      await this._updateImages();
      this._update();
    });

    await this._updateImages(true);
  }

  async _updateImages(initial = false) {
    const breakpoint = getBreakpoint();
    const state = this._getState();

    try {
      this._disable();
      this.images = await this._loadScenarioImages(breakpoint, state.product, state.scenario);
      this.imageNode = this.images[`${state.product}_${state.scenario}_${state.year}`];
      initial
        ? this.figureNode.appendChild(this.imageNode)
        : this.figureNode.replaceChild(this.imageNode, this.figureNode.firstElementChild);
      this._enable();
    } catch (err) {
      console.error('Error:', err);
    }
  }

  async _loadScenarioImages(breakpoint, product, scenario) {
    const imageAssets = this.years.map((year) => {
      return {
        id: `${product}_${scenario}_${year}`,
        url: imageUrls[`./images/${breakpoint}-optimized/${product}_${scenario}_${year}.png`],
      };
    });

    try {
      const images = {};
      for (const imageAsset of imageAssets) {
        images[imageAsset.id] = await loadImage(imageAsset.url);
      }
      return images;
    } catch (err) {
      console.error('Error:', err);
    }
  }

  _enable() {
    this.ariaBusy = 'false';
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      fieldset.disabled = false;
    });
  }

  _disable() {
    this.ariaBusy = 'true';
    this.querySelectorAll('fieldset').forEach((fieldset) => {
      fieldset.disabled = true;
    });
  }

  async _handleChange(event) {
    if (event.target.name === 'product' || event.target.name === 'scenario') {
      this._stopPlayback();
      await this._updateImages();
      this._update();
      return;
    }

    this._update();
  }

  _handleInput() {
    this._update();
  }

  _handleClickPlay() {
    if (this.interval) {
      this._stopPlayback();
    } else {
      this._startPlayback();
    }
  }

  _getState() {
    const product = this.querySelector('input[name="product"]:checked').getAttribute('value');
    const scenario = this.querySelector('input[name="scenario"]:checked').getAttribute('value');
    const year = parseInt(this.yearInputNode.value, 10);
    const yearMin = parseInt(this.yearInputNode.getAttribute('min'), 10);
    const yearMax = parseInt(this.yearInputNode.getAttribute('max'), 10);
    const yearStep = parseInt(this.yearInputNode.getAttribute('step'), 10);
    const yearSpan = yearMax - yearMin;

    return { product, scenario, year, yearMin, yearMax, yearStep, yearSpan };
  }

  _update() {
    const state = this._getState();

    const image = this.images[`${state.product}_${state.scenario}_${state.year}`];
    this.figureNode.replaceChild(image, this.figureNode.firstElementChild);

    this.tooltipNode.textContent = state.year;
    this.tooltipNode.style.left = `${((state.year - state.yearMin) / state.yearSpan) * 100}%`;

    this.keyNode.querySelectorAll(`*[data-ref]`).forEach((x) => x.setAttribute('hidden', ''));
    this.keyNode.querySelector(`*[data-ref="key-${state.product}"]`).removeAttribute('hidden');

    this.yAxisNode.querySelectorAll(`*[data-ref]`).forEach((x) => x.setAttribute('hidden', ''));
    this.yAxisNode.querySelector(`*[data-ref="label-${state.product}"]`).removeAttribute('hidden');

    this.xAxisNode.querySelectorAll(`*[data-ref]`).forEach((x) => x.setAttribute('hidden', ''));
    this.xAxisNode.querySelector(`*[data-ref="label-${state.product}"]`).removeAttribute('hidden');
  }

  _startPlayback() {
    this.interval = setInterval(this._tickPlayback.bind(this), 500);

    this.playButtonNode.querySelector('[data-ref="playback-paused"]').setAttribute('hidden', '');
    this.playButtonNode.querySelector('[data-ref="playback-playing"]').removeAttribute('hidden');
  }

  _stopPlayback() {
    clearInterval(this.interval);
    this.interval = null;

    this.playButtonNode.querySelector('[data-ref="playback-playing"]').setAttribute('hidden', '');
    this.playButtonNode.querySelector('[data-ref="playback-paused"]').removeAttribute('hidden');
  }

  _tickPlayback() {
    const state = this._getState();

    if (state.year >= state.yearMax) {
      this.yearInputNode.value = state.yearMin;
      this._update();
      return;
    }

    this.yearInputNode.value = state.year + state.yearStep;
    this._update();
  }
}

window.customElements.define('boga-supply-curve-chart', BogaSupplyCurveChart);
